/**
 * cards.ts —— 52 张牌的编码、解析与格式化
 *
 * 编码约定（全引擎唯一口径，PRD 1.1）：
 *   card ∈ [0, 52)，rank = card >>> 2 ∈ [0,13)，suit = card & 3 ∈ [0,4)
 *   rank 0 = 2，rank 12 = A（A 在轮子顺子 A2345 中另作低张处理，见 evaluator.ts）
 *
 * 为什么这样编码：
 *   1. `>>> 2` / `& 3` 是单指令，比 rank*4+suit 的除模运算快；
 *   2. rank 连续递增 ⇒ 点数位图直接用 `1 << rank`，评估器可纯位运算；
 *   3. 同点数的 4 张牌在数值上相邻 ⇒ 牌堆按 rank 分组时 cache 友好。
 */

/** 牌的整数编码，取值 0..51。用 branded 类型避免和普通 number 混用。 */
export type Card = number;

/** 点数序号 0..12，0 = 2，12 = A。 */
export type Rank = number;

/** 花色序号 0..3，见 SUIT_SYMBOLS / SUIT_LETTERS。 */
export type Suit = number;

export const NUM_CARDS = 52;
export const NUM_RANKS = 13;
export const NUM_SUITS = 4;

/** 点数字符，索引即 rank。 */
export const RANK_LETTERS = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'] as const;
/** 花色字母，索引即 suit：c=梅花 d=方块 h=红桃 s=黑桃。 */
export const SUIT_LETTERS = ['c', 'd', 'h', 's'] as const;
/** 花色符号，供 UI 直接展示。 */
export const SUIT_SYMBOLS = ['♣', '♦', '♥', '♠'] as const;
/** 点数中文名，供 UI 文案拼接（如「两对（A/9，踢脚 K）」）。 */
export const RANK_NAMES_CN = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'] as const;

export function makeCard(rank: Rank, suit: Suit): Card {
  return (rank << 2) | suit;
}

export function rankOf(card: Card): Rank {
  return card >>> 2;
}

export function suitOf(card: Card): Suit {
  return card & 3;
}

/** 全新有序牌堆 [0..51]，调用方自行洗牌。 */
export function createDeck(): Card[] {
  const deck: Card[] = new Array(NUM_CARDS);
  for (let i = 0; i < NUM_CARDS; i++) deck[i] = i;
  return deck;
}

/** 格式化为 "As" / "Td" 形式。 */
export function formatCard(card: Card): string {
  return RANK_LETTERS[rankOf(card)] + SUIT_LETTERS[suitOf(card)];
}

/**
 * 展示层点数字符（R6，第 9 轮）：把内部编码里的 `T` 一律显示为 `10`，其余点数原样。
 *
 * 这是**展示层唯一入口**：所有面向用户的牌面点数（公共牌 / 底牌 / 对手摊牌 / 复盘 /
 * 手牌历史 / 决策评分 / 牌型描述）都应经过它，避免 `T` 散落各处漏改。
 * 核心引擎 / 解析 / 存档仍用 `RANK_LETTERS`（`formatCard` / `parseCard` / `handClassLabel`），
 * 内部表示不变——只有"给人看"的那一层统一成 `10`。
 *
 * 实现直接复用早已存在的 `RANK_NAMES_CN`（索引 8 = `'10'`），因此天然满足：
 *   - `T` → `'10'`；
 *   - 已存在的字符 `'10'` 不被二次改写（这里只做一次查表，不做字符串替换）；
 *   - `A/K/Q/J` 等其它点数不受影响。
 */
export function rankDisplay(rank: Rank): string {
  return RANK_NAMES_CN[rank];
}

/** 格式化为 "A♠" 形式，供 UI 展示；点数走 `rankDisplay`（T 显示为 10）。 */
export function formatCardSymbol(card: Card): string {
  return rankDisplay(rankOf(card)) + SUIT_SYMBOLS[suitOf(card)];
}

/** 展示层牌面（点数 + 花色符号），点数经 `rankDisplay`（T→10）。等价于 `formatCardSymbol`，语义更显式。 */
export function formatCardDisplay(card: Card): string {
  return rankDisplay(rankOf(card)) + SUIT_SYMBOLS[suitOf(card)];
}

export function formatCards(cards: readonly Card[]): string {
  return cards.map(formatCard).join(' ');
}

/**
 * 解析 "As"、"Td"、"2C"（大小写不敏感）为 Card。
 * 非法输入抛错而不是返回 -1 —— 牌序注入（PRD 8.5）必须尽早失败，
 * 否则一个拼错的牌名会静默产生错误牌局。
 */
export function parseCard(text: string): Card {
  const s = text.trim();
  if (s.length !== 2) throw new Error(`非法牌名 "${text}"：应为 2 个字符，如 "As"`);
  const r = RANK_LETTERS.indexOf(s[0].toUpperCase() as (typeof RANK_LETTERS)[number]);
  const u = SUIT_LETTERS.indexOf(s[1].toLowerCase() as (typeof SUIT_LETTERS)[number]);
  if (r < 0) throw new Error(`非法点数 "${s[0]}"，合法值：${RANK_LETTERS.join('')}`);
  if (u < 0) throw new Error(`非法花色 "${s[1]}"，合法值：${SUIT_LETTERS.join('')}`);
  return makeCard(r, u);
}

/** 解析 "As Kd 7h" 或 "AsKd7h" 为 Card[]。 */
export function parseCards(text: string): Card[] {
  const trimmed = text.trim();
  if (trimmed === '') return [];
  const tokens = trimmed.includes(' ') || trimmed.includes(',')
    ? trimmed.split(/[\s,]+/).filter(Boolean)
    : (trimmed.match(/.{1,2}/g) ?? []);
  return tokens.map(parseCard);
}

/** 断言一组牌合法且无重复；用于牌序注入与赢率入参校验。 */
export function assertDistinctCards(cards: readonly Card[], context = 'cards'): void {
  const seen = new Set<Card>();
  for (const c of cards) {
    if (!Number.isInteger(c) || c < 0 || c >= NUM_CARDS) {
      throw new Error(`${context}: 非法牌编码 ${c}，合法范围 0..51`);
    }
    if (seen.has(c)) throw new Error(`${context}: 重复的牌 ${formatCard(c)}`);
    seen.add(c);
  }
}

/**
 * 起手牌等价类标签（169 类之一）：对子 "AA"、同花 "AKs"、非同花 "AKo"。
 * 翻牌前离线表查表键，也是 HUD / 复盘展示用的标准写法。
 */
export function handClassLabel(a: Card, b: Card): string {
  const ra = rankOf(a);
  const rb = rankOf(b);
  const hi = Math.max(ra, rb);
  const lo = Math.min(ra, rb);
  if (ra === rb) return RANK_LETTERS[hi] + RANK_LETTERS[lo];
  const suited = suitOf(a) === suitOf(b);
  return RANK_LETTERS[hi] + RANK_LETTERS[lo] + (suited ? 's' : 'o');
}

/**
 * 169 个等价类标签的规范顺序：13 对子 → 78 同花 → 78 非同花。
 * 与 gen_preflop.c 的 build_classes() 顺序严格一致，用于二进制表的下标映射。
 */
export function allHandClassLabels(): string[] {
  const out: string[] = [];
  for (let r = 12; r >= 0; r--) out.push(RANK_LETTERS[r] + RANK_LETTERS[r]);
  for (const suffix of ['s', 'o'] as const) {
    for (let hi = 12; hi >= 1; hi--) {
      for (let lo = hi - 1; lo >= 0; lo--) {
        out.push(RANK_LETTERS[hi] + RANK_LETTERS[lo] + suffix);
      }
    }
  }
  return out;
}
